/**
 * English translations. Source of truth for locale key parity.
 * Keep nested objects consistent with `pt.ts`.
 *
 * The `i18next` interpolation uses `{{name}}`.
 */
export const en = {
  appName: "Sprint",
  common: {
    loading: "Loading…",
    error: "Something went wrong",
    retry: "Retry",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    remove: "Remove",
    close: "Close",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    back: "Back",
    next: "Next",
    previous: "Previous",
    done: "Done",
    submit: "Submit",
    search: "Search",
    filter: "Filter",
    reset: "Reset",
    refresh: "Refresh",
    copy: "Copy",
    copied: "Copied",
    empty: "Nothing here yet.",
    notAvailable: "N/A",
    unknown: "Unknown",
    open: "Open",
    add: "Add",
    create: "Create",
    remove_confirm: "Are you sure you want to remove this?",
    notFoundTitle: "Page not found",
    notFoundBody: "The page you are looking for does not exist.",
    backHome: "Back to home",
    english: "English",
    portuguese: "Português",
    consoleSubtitle: "Operator console",
    adminSubtitle: "Administration",
    all: "All",
    none: "None",
    optional: "optional",
    required: "required",
    from: "From",
    to: "To",
    range: "Range",
    today: "Today",
    id: "ID",
    details: "Details",
    more: "More",
  },
  theme: {
    light: "Light",
    dark: "Dark",
    system: "System",
    toggle: "Toggle theme",
  },
  nav: {
    dashboard: "Matchday",
    matches: "Matches",
    teams: "Teams",
    players: "Players",
    sync: "Updates",
    club: "Club",
    kits: "Kits",
    admin: "Admin",
    logout: "Sign out",
    settings: "Settings",
    language: "Language",
    primary: "Primary navigation",
    openMenu: "Open menu",
  },
  login: {
    title: "Welcome to Sprint",
    clubLabel: "Emjogo URL",
    clubDescription: "The URL you use to access your club’s EmJogo.",
    usernameLabel: "EmJogo username",
    passwordLabel: "EmJogo password",
    submit: "Sign in",
    loadingSubmit: "Signing in…",
    clubLink: "Sign in as club operator",
    errors: {
      invalid: "Invalid username or password.",
      notAuthorized:
        "This club is not authorized to use Sprint. Contact the operator if this is a mistake.",
      usernameRequired: "Username is required.",
      passwordRequired: "Password is required.",
      clubRequired: "Please enter your club’s EmJogo URL.",
    },
  },
  admin: {
    loginTitle: "Administrator sign-in",
    loginSubtitle: "Manage which clubs can use Sprint.",
    usernameLabel: "Administrator username",
    passwordLabel: "Administrator password",
    clubs: {
      title: "Authorized clubs",
      subtitle: "Add, update, or suspend clubs that can access Sprint.",
      add: "Add club",
      clubname: "Slug",
      displayName: "Display name",
      notes: "Notes",
      active: "Active",
      authorizedAt: "Authorized",
      lastLogin: "Last login",
      updatedAt: "Updated",
      empty: "No clubs authorized yet. Add your first one above.",
      createDialogTitle: "Authorize a new club",
      createDialogDescription:
        "The slug must match the club’s EmJogo identifier exactly.",
      editDialogTitle: "Update club",
      suspendConfirm:
        "Are you sure? Suspending a club immediately blocks operators from signing in.",
      statusActive: "Active",
      statusSuspended: "Suspended",
      authorizedBy: "Authorized by",
      createdBy: "Authorized by {{name}}",
      neverLoggedIn: "Never logged in",
      suspend: "Suspend",
      activate: "Activate",
      errors: {
        duplicate: "A club with that slug is already authorized.",
        empty: "At least one field must be provided.",
        notFound: "Club not found.",
      },
      toast: {
        created: "Club authorized.",
        updated: "Club updated.",
        suspended: "Club suspended.",
        activated: "Club reactivated.",
      },
    },
  },
  dashboard: {
    title: "Matches",
    subtitle:
      "Your team’s upcoming and recent fixtures, with submission status.",
    filters: {
      dateRange: "Date range",
      allTeams: "All teams",
      team: "Team",
      competition: "Competition",
      submission: "Submission",
      submitted: "Submitted to Score",
      notSubmitted: "Not submitted",
      upcoming: "Upcoming",
      past: "Past",
      clear: "Clear filters",
    },
    columns: {
      date: "Date",
      match: "Match",
      venue: "Venue",
      competition: "Competition",
      status: "Status",
      actions: "Actions",
    },
    venueHome: "Home",
    venueAway: "Away",
    vs: "vs",
    at: "at",
    openSheet: "Open match sheet",
    empty: "No matches in this range. Try widening the dates or syncing data.",
    refreshAll: "Refresh match-day data",
    refreshAllDescription:
      "Pulls the latest match information from EmJogo for every upcoming fixture.",
    refreshSubmitted: "Check Score submissions",
    today: "Today",
    thisWeek: "This week",
    thisMonth: "This month",
    nextMonth: "Next month",
  },
  matches: {
    sheet: {
      title: "Match sheet",
      breadcrumb: "Match sheet · {{match}}",
      modeSinglePage: "Single page",
      modeWizard: "Guided wizard",
      modeHelp: "Switch between dense editing and a guided flow.",
      refreshFromEmjogo: "Update from EmJogo",
      refreshFromEmjogoHint:
        "Re-fetch this match's details and lineup from EmJogo.",
      sections: {
        summary: "Match",
        kit: "Kit",
        staff: "Staff",
        roster: "Roster",
        starters: "Starters",
        bench: "Bench",
        submission: "Submission to Score",
      },
      kit: {
        none: "No kit selected",
        change: "Change kit",
        select: "Select kit",
        makeDefault: "Use as main",
        oneOffDescription:
          "Pick colors for this match only, without changing your saved kits.",
        saveKit: "Save this as a reusable kit",
        outfield: {
          sectionTitle: "Outfield kit",
          oneOffTitle: "One-off outfield kit",
          oneOffLabel: "One-off kit",
          addOneOff: "One-off colors",
        },
        goalkeeper: {
          sectionTitle: "Goalkeeper kit",
          oneOffTitle: "One-off goalkeeper kit",
          oneOffLabel: "One-off kit",
          addOneOff: "One-off colors",
          none: "No goalkeeper kit",
        },
      },
      staff: {
        copyPrevious: "Copy from last match",
        copySource: "Copied from {{opponent}} ({{date}})",
        emptyEmjogo: "No club staff approved on Score.",
        emptyScore: "No club staff approved on Score.",
        function: "Function",
        banco: "Bench staff",
        custom: "Custom role",
        picker: "Select staff",
        saveChanges: "Save staff",
        search: "Search by name or role…",
        searchHint: "Search spans every category.",
        selectedCount: "{{count}} selected",
        selectedTitle: "Selected",
        selectedEmpty:
          "No staff selected yet. Pick a category below or search to add the people that will sit on the bench.",
        availableTitle: "Add staff",
        availableCount: "{{count}} available",
        add: "Add",
        addAria: "Add {{name}} to match sheet",
        removeAria: "Remove {{name}} from match sheet",
        noMatchInCategory: "No staff in this category yet.",
        noSearchMatches: 'No staff match "{{query}}".',
        allInCategoryAdded: "Everyone in this category is on the sheet.",
        categories: {
          coaches: "Coaches",
          delegates: "Delegates",
          medical: "Medical",
          others: "Others",
        },
        functions: {
          trainerMain: "Head coach",
          trainerAssistant: "Assistant coach",
          physicalCoach: "Physical coach",
          delegate1: "1st Delegate",
          delegate2: "2nd Delegate",
          medic: "Doctor",
          masseur: "Masseur",
          nurse: "Nurse",
          trainerIntern: "Trainee coach",
          physiotherapist: "Physiotherapist",
          other: "Other",
          sbvTechnician: "BLS-AED Technician",
        },
      },
      players: {
        role: "Role",
        roleC: "Captain",
        roleSC: "Vice-captain",
        number: "Number",
        numberPlaceholder: "#",
        license: "License",
        resolved: "Resolved",
        ambiguous: "Ambiguous",
        unresolved: "Not resolved",
        crossTeam: "Other team",
        pick: "Choose player",
        picker: "Select a player",
        pickerHint: "Search by name, number, or license.",
        preview: "Player details: {{name}}",
        manualNote: "Entered by hand on EmJogo",
        noCandidates: "No matching players found.",
      },
      submission: {
        notLinked: "This match is not linked to Score yet.",
        linkScore: "Link to Score",
        unlinkScore: "Unlink from Score",
        save: "Save to Score",
        submit: "Submit to Score",
        submitted: "Submitted to Score",
        reverify: "Re-verify",
        openReport: "Open match-sheet PDF",
        openScore: "Open on Score",
        invalidLicenseTitle: "Players without a valid license",
        invalidLicenseBody:
          "Resolve the players below before submitting the sheet.",
        diffTitle: "Differences vs Score",
        diffOk: "The submitted sheet matches what Score has on file.",
        diffExtra: "Extra in Score",
        diffMissing: "Missing in Score",
        diffMismatched: "Mismatched",
        diffError:
          "We could not verify the submission against Score ({{detail}}).",
      },
      linkDialog: {
        title: "Link to a Score match",
        description:
          "Pick the Score fixture that matches this EmJogo match. We will use it to submit the sheet.",
        empty: "No Score matches available for this team.",
        refresh: "Refresh Score matches",
        chooseTeam: "Choose your team on Score",
        chooseMatch: "Choose the opposing Score match",
      },
      modeAria: "Sheet layout",
      wizard: {
        back: "Back",
        next: "Next",
        progress: "Step {{current}} of {{total}}",
        stepsAria: "Match-sheet steps",
        linkKitDescription:
          "Link this match to Score and pick the kit the team will wear.",
        rosterDescription:
          "Review the starting eleven and bench side by side: resolve ambiguous entries, set numbers, and assign the captain and vice-captain.",
        staffDescription:
          "Pick the staff that will be on the match sheet, with their functions.",
        reviewDescription:
          "Final step: save the sheet to Score, submit it, and verify.",
        step: {
          linkKit: "Match & kit",
          roster: "Roster",
          staff: "Staff",
          review: "Review & submit",
        },
        summary: {
          title: "Match sheet summary",
          link: "Score",
          notLinked: "Not linked yet",
          linkedAs: "Score #{{id}}",
          kit: "Kit",
          noKit: "No kit selected",
          gkKit: "Goalkeeper kit",
          starters: "Starters",
          bench: "Bench",
          startersCount: "{{count}} starter",
          startersCount_other: "{{count}} starters",
          benchCount: "{{count}} substitute",
          benchCount_other: "{{count}} substitutes",
          captain: "Captain: {{name}}",
          viceCaptain: "Vice-captain: {{name}}",
          unresolvedWarning: "{{count}} unresolved",
          ambiguousWarning: "{{count}} ambiguous",
          staff: "Staff",
          staffCount: "{{count}} selected",
          staffCount_other: "{{count}} selected",
          noStaff: "No staff selected",
          bench_staff: "Bench staff: {{count}}",
        },
      },
    },
  },
  teams: {
    title: "Teams",
    subtitle: "Every squad synced from EmJogo, with licensed player counts.",
    searchPlaceholder: "Search by team, level, or category",
    openDetail: "Open team",
    scoreLinked: "Linked to Score",
    scoreNotLinked: "Not linked",
    category: "Category",
    empty: "No teams synced yet.",
    emptyHint: "Run a full club sync to pull teams from EmJogo.",
    emptyFiltered: "No teams match the current filters.",
    clearFilters: "Clear filters",
    filters: {
      level: "Level",
      allLevels: "All levels",
    },
    stats: {
      players: "Players",
      matches: "Matches",
    },
    detail: {
      backToTeams: "All teams",
      syncFromEmjogo: "Update from EmJogo",
      rosterTitle: "Roster",
      rosterDescription: "Licensed players EmJogo has on file for this team.",
      rosterSearchPlaceholder: "Search players…",
      noPlayers: "No players on this roster yet.",
      noPlayersDescription:
        "Run a team or full-club sync to pull the latest licensed squad.",
      noMetadata: "No metadata synced for this team yet.",
      notFound: "Team not found",
      notFoundHint:
        "This team may have been removed or does not belong to your club.",
    },
  },
  players: {
    title: "Players",
    subtitle: "{{count}} licensed player across all teams.",
    subtitle_other: "{{count}} licensed players across all teams.",
    searchPlaceholder: "Search by name, number, license, or team",
    empty: "No players synced yet.",
    emptyHint: "Run a full club sync to pull the licensed squad from EmJogo.",
    noMatches: "No players match the current filters.",
    columns: {
      name: "Player",
      team: "Team",
      position: "Position",
      license: "License",
    },
    filters: {
      position: "Position",
      allPositions: "All positions",
    },
  },
  demo: {
    banner: "Demo mode — all data is fictional",
    reset: {
      run: "Reset demo",
      toastSuccess: "Demo reset — the club is empty again.",
    },
    seed: {
      run: "Load demo data",
      toastSuccess:
        "Loaded {{teams}} teams, {{players}} players and {{matches}} matches.",
    },
  },
  debug: {
    title: "Debug data",
    subtitle:
      "Local-dev-only tools for logging in, resetting and seeding test data.",
    login: {
      title: "Log in as test club",
      description:
        "Sign in as the test club through the real auth pipeline against emjogo-mock, including an automatic Score (score-mock) session — syncs, jobs and submissions all work.",
      run: "Log in as test club",
      toastSuccess: "Logged in as test club.",
    },
    reset: {
      title: "Reset database",
      description: "Wipe every collection in the local database.",
      run: "Reset database",
      toastSuccess: "Database reset.",
    },
    seed: {
      title: "Load mock data",
      description:
        "Load the mock fixture world (teams, players, staff, matches) directly into the database, including Score links — without running the sync pipeline. Reset first if data was seeded before an id-scheme change.",
      run: "Load mock data",
      toastSuccess:
        "Loaded {{teams}} teams, {{players}} players, {{matches}} matches ({{linkedMatches}} Score-linked).",
    },
  },
  sync: {
    title: "Updates",
    subtitle:
      "Sprint keeps a copy of your club's data from EmJogo. When something changes there, bring it into Sprint from here.",
    requires: "Requires {{systems}} sign-in.",
    lastRun: "Last run {{when}}",
    neverRun: "Not run yet.",
    matches: {
      title: "Update matches & lineups",
      description:
        "Get the latest matches, call-ups and lineups from EmJogo. Run this before preparing a match sheet.",
      run: "Update matches",
    },
    full: {
      title: "Full sync",
      description:
        "Re-import everything — teams, players, staff and licenses — and link it all with Score. Use it when players or teams changed in EmJogo, or when something looks off. It takes a few minutes.",
      run: "Run full sync",
    },
    advanced: {
      title: "Advanced options",
      description:
        "Narrower tools for specific fixes — a full sync already covers all of this.",
    },
    team: {
      title: "Update one team",
      description:
        "Re-import matches, staff and licensed players for a single team.",
      run: "Update team",
      teamLabel: "Team",
      teamPlaceholder: "Choose a team",
    },
    licenses: {
      title: "Fill in missing licenses",
      description:
        "Look up license numbers in EmJogo for players that don't have one yet.",
      run: "Fetch licenses",
    },
    activity: {
      title: "Recent activity",
      description:
        "Every update runs in the background. Open an entry to see its details.",
    },
  },
  jobs: {
    empty: "No activity yet. Run your first update above.",
    columns: {
      type: "Type",
      status: "Status",
      created: "Created",
      started: "Started",
      finished: "Finished",
      duration: "Duration",
      progress: "Progress",
      error: "Error",
    },
    type: {
      full_sync_club: "Full club sync",
      sync_team: "Sync team",
      refresh_all_matches: "Refresh all matches",
      refresh_match: "Refresh one match",
      sync_missing_licenses: "Sync missing licenses",
    },
    status: {
      queued: "Queued",
      running: "Running",
      succeeded: "Succeeded",
      partial_failure: "Partial failure",
      failed: "Failed",
      cancelled: "Cancelled",
    },
    stepStatus: {
      pending: "Pending",
      running: "Running",
      succeeded: "Succeeded",
      failed: "Failed",
      skipped: "Skipped",
    },
    details: {
      title: "Job · {{id}}",
      params: "Parameters",
      steps: "Steps",
      result: "Result",
      error: "Error",
      cancel: "Cancel job",
      cancelConfirm:
        "Cancel this job? Work already running will run to completion.",
      cancelSuccess: "Job cancelled.",
      cancelFailed:
        "Could not cancel this job — it may already be running or finished.",
    },
    duration: "{{value}}",
    stepKey: {
      resolve_teams: "Resolve teams",
      sync_matches: "Sync matches",
      sync_athletes: "Sync athletes",
      sync_licenses: "Sync licenses",
      link_score: "Link to Score",
      sync_staff: "Sync staff",
      sync_score_match: "Sync Score match",
    },
  },
  score: {
    reauthTitle: "Sign in to Score to continue",
    reauthDescription:
      "This action needs a fresh Score session. Enter your credentials to continue.",
    usernameLabel: "Score username",
    passwordLabel: "Score password",
    codeLabel: "Verification code",
    codeHint: "Enter the one-time code sent by Score.",
    startLogin: "Send verification code",
    verify: "Verify",
    resend: "Resend code",
    expiresSoon: "Your Score session expires in {{minutes}} minutes.",
    signedIn: "Signed in to Score.",
    signOut: "Sign out of Score",
    errors: {
      invalid: "Invalid username or password.",
      invalidCode: "Invalid or expired verification code.",
      upstream: "We could not reach Score. Please try again in a moment.",
    },
  },
  club: {
    profile: {
      title: "Club profile",
      subtitle: "Information synced from EmJogo.",
      fullname: "Full name",
      name: "Short name",
      shortname: "Badge abbreviation",
      notSyncedYet:
        "Club data has not been synced from EmJogo yet. Run a full sync to fill in the name, crest, and team list.",
    },
    kits: {
      edit: "Edit",
      nameLabel: "Kit name",
      shirt: "Shirt",
      shorts: "Shorts",
      socks: "Socks",
      outfield: {
        title: "Outfield kits",
        subtitle: "Field-player kits your teams wear on match day.",
        add: "Add outfield kit",
        edit: "Edit outfield kit",
        empty: "No outfield kits yet. Add one to use it on match sheets.",
      },
      goalkeeper: {
        title: "Goalkeeper kits",
        subtitle: "Goalkeeper strips, chosen separately for each match.",
        add: "Add goalkeeper kit",
        edit: "Edit goalkeeper kit",
        empty: "No goalkeeper kits yet.",
      },
      default: "Main",
      setDefault: "Set as main",
      clearDefault: "Clear main",
      deleteConfirm:
        "Delete this kit? Match sheets that use it will be unlinked.",
      empty: "No kits yet. Add your first kit to use it on match sheets.",
      toast: {
        created: "Kit added.",
        updated: "Kit updated.",
        deleted: "Kit deleted.",
        defaultSet: "Main kit updated.",
        defaultCleared: "Main kit cleared.",
      },
      color: {
        Red: "Red",
        Black: "Black",
        Yellow: "Yellow",
        White: "White",
        Blue: "Blue",
        Green: "Green",
        Golden: "Golden",
        Orange: "Orange",
        Gray: "Gray",
        Maroon: "Maroon",
        "White-Blue": "White / Blue",
        Lilac: "Lilac",
        Pink: "Pink",
        "White-Black": "White / Black",
      },
    },
  },
  errors: {
    title: "Unable to load",
    networkTitle: "Offline or server unavailable",
    networkBody:
      "We could not reach Sprint. Check your connection and try again.",
    genericBody: "An unexpected error happened. Please try again.",
    unauthorizedTitle: "Session expired",
    unauthorizedBody: "Sign in again to continue.",
    forbiddenTitle: "Not authorized",
    forbiddenBody: "Your account cannot access this resource.",
    sessionMissingTitle: "Session missing",
    sessionMissingBody: "Please log in again so we can reach your club safely.",
  },
  footer: {
    status: "All systems operational",
    build: "Build {{version}}",
    docs: "Docs",
  },
} as const;

/**
 * Broaden every leaf from a literal string into `string` so that other locales
 * (e.g., pt.ts) can supply their own translations without the compiler insisting
 * on the English literal.
 */
type DeepStringify<T> = T extends string
  ? string
  : T extends ReadonlyArray<infer U>
    ? DeepStringify<U>[]
    : T extends object
      ? { -readonly [K in keyof T]: DeepStringify<T[K]> }
      : T;

export type TranslationShape = DeepStringify<typeof en>;
